import styled from 'styled-components';
import { useState, useEffect } from 'react';
import BdoModal from '../../shared/BdoModal';
import useMediaQuery from '../../custom-hooks/useMediaQuery';
import { device } from '../../../utils/styled-component-mixins';
import InsightsSectionComponent from '../InsightsSectionComponent';

const ServiceLandingTabContentStyled = styled.div`
  .intro-content {
    background: linear-gradient(180deg, #e6e9ec 0%, #ffffff 100%);
    margin-bottom: 5rem;
    padding-top: 4rem;

    &-row {
      @media ${device.desktop} {
        display: flex;
        gap: 9rem;

        div:last-child {
          flex: 2 0 60%;
        }
      }
    }

    .btn {
      font-size: 1.4rem;
      margin-top: 2rem;
    }

    .intro-content-description {
      line-height: 2.4rem;

      @media ${device.desktop} {
        width: 40%;
      }
    }

    figure {
      width: 60%;
    }
  }

  .resources-filter-wrapper {
    display: flex;

    .filter-label {
      color: var(--black);
      margin: 0 1rem 0 0;
    }
  }

  .swiper-pagination {
    max-width: 36rem;
    left: 50%;
    transform: translateX(-50%);
  }

  .swiper {
    margin-top: 0;
    padding-bottom: 9.2rem;
    padding-top: 2.4rem;

    @media ${device.desktop} {
      padding-top: 4rem;
    }

    .widget-spacing-wrapper {
      margin-bottom: 0;
    }

    &:hover {
      .swiper-button-next:not(.swiper-button-disabled),
      .swiper-button-prev:not(.swiper-button-disabled) {
        opacity: 1;
      }

      .swiper-button-disabled {
        opacity: 0.35;
      }
    }

    .swiper-slide {
      height: auto;

      @media ${device.desktop} {
        max-width: 25%;
      }
    }

    .swiper-button-next,
    .swiper-button-prev {
      display: none;

      @media ${device.desktop} {
        background-color: var(--grey);
        border-radius: 50%;
        display: block;
        height: 5.3rem;
        width: 5.3rem;
        opacity: 0;
        transition: all 0.3s linear;
      }

      &:after {
        color: var(--charcoal-2);
        font-size: 2.5rem;
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
      }
    }

    .swiper-button-prev {
      left: 0.55rem;
    }

    .swiper-button-next {
      right: 0.55rem;
    }

    .swiper-pagination {
      bottom: 4.3rem;
    }
  }

  div[class^='InsightsSectionComponent'] {
    .select-input-wrapper {
      @media ${device.deviceOnly} {
        justify-content: flex-start;
        padding: 0 1.5rem;
      }
    }
  }

  div[class*='CardSwiperWrapperStyled'] {
    .container-padding-md {
      background-color: transparent;
    }

    &:before,
    &:after {
      content: none;
    }

    .swiper {
      padding-top: 0;
    }
  }
`;

const ModalContentStyled = styled.div`
  @media ${device.desktop} {
    align-items: center;
    display: flex;
    flex-direction: column;
  }

  .btn-primary {
    line-height: 1.8rem;
    margin: 5.6rem auto 0 auto;

    @media ${device.desktop} {
      line-height: 2.4rem;
      margin: 8rem 0 0;
    }
  }

  .modal-description {
    font-size: 2.2rem;
    line-height: 3.2rem;

    @media ${device.desktop} {
      font-size: 2.6rem;
      line-height: 4rem;
      text-align: center;
    }
  }

  .services-list {
    width: 100%;

    &-ellipses {
      color: var(--cta-blue);
      font-size: 1.6rem;
      font-weight: 700;
      line-height: 2.4rem;
      margin: 1.6rem 0 0 0;
    }

    &-toggle {
      margin-top: 1.6rem;

      span:last-child {
        font-size: 2rem;
        margin-bottom: 0.1rem;
        margin-left: 0.3rem;
      }
    }

    ul {
      display: grid;
      grid-template-columns: 100%;
      margin-top: 4.7rem;
      row-gap: 1.6rem;
      width: 100%;

      @media ${device.desktop} {
        display: grid;
        grid-template-columns: 50% 50%;
        margin-top: 6.4rem;
      }

      .btn-tertiary > span:first-child {
        font-size: 1.6rem;
      }
    }

    figure {
      width: 60%;
    }
  }
`;

const ServiceLandingTabContent = (props) => {
  const {
    item,
    serviceAreaIndex,
    tabModalActivationText,
    activeTab,
    modalCtaText,
    modalListExpand,
    modalListCollapse,
    filterLabelText
  } = props;
  const [showModal, setShowModal] = useState(false);
  const [isServiceListHidden, setIsServiceListHidden] = useState(true);
  const isDesktop = useMediaQuery(device.desktop);

  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    setTestimonialIndex(Math.floor(Math.random() * item.testimonials.length));
  }, [item.testimonials]);

  const formatStringWithServiceAreaName = (serviceAreaName, string) =>
    string.replace('{0}', serviceAreaName);

  return (
    <ServiceLandingTabContentStyled
      aria-labelledby={item.pageTitle}
      key={`${item.pageTitle}-${serviceAreaIndex}-tab`}
      role="tabpanel"
      id={`${item.pageTitle}-tab`}
      className={`${serviceAreaIndex !== 0 && 'is-hidden'}`}
    >
      <div className="intro-content container-padding-md">
        <div className="intro-content-row">
          <div className="intro-content-description">{item.description}</div>
          {item.testimonials.length > 0 && isDesktop && (
            <figure>
              <blockquote>
                <div
                  dangerouslySetInnerHTML={{
                    __html: item.testimonials[testimonialIndex].quote
                  }}
                ></div>
                <figcaption className="author">
                  <cite>
                    - {item.testimonials[testimonialIndex].author}
                    {item.testimonials[testimonialIndex].authorTitle &&
                      `, ${item.testimonials[testimonialIndex].authorTitle}`}
                  </cite>
                </figcaption>
              </blockquote>
            </figure>
          )}
        </div>
        <ButtonLink
          isButtonTag={true}
          theme="light"
          type="tertiary"
          text={formatStringWithServiceAreaName(item.pageTitle, tabModalActivationText)}
          onClickFunction={() => setShowModal(true)}
          widgetID={formatStringWithServiceAreaName(item.pageTitle, tabModalActivationText)}
        />
      </div>
      {(activeTab !== '' || !isDesktop) && item.insightSection && (
        <InsightsSectionComponent
          items={item.insightSection.items}
          properties={item.insightSection.properties}
          featuredItems={item.insightSection.featuredItems}
          resourceStrings={item.insightSection.resourceStrings}
          marginTop={item.insightSection.properties.marginTop}
          marginBottom={item.insightSection.properties.marginBottom}
          filterOptions={item.insightSection.filterOptions}
          selectedFilter={item.insightSection.selectedFilter}
          insightsSection={item.insightSection.insightsSection}
          selectedIndustry={item.insightSection.selectedIndustry}
          filterLabel={formatStringWithServiceAreaName(item.pageTitle, filterLabelText)}
        />
      )}
      <BdoModal
        showModal={showModal}
        setShowModal={setShowModal}
        serviceAreaTitle={item.pageTitle}
        classProp="bdo-modal one-cta"
      >
        <ModalContentStyled>
          <h2>{item.pageTitle}</h2>
          <div>
            <p className="modal-description">{item.modalDescription}</p>
          </div>
          <div className="services-list">
            <ul className="non-bdo-style-list">
              {isDesktop &&
                item.services.map((service) => (
                  <li key={service.nodeID}>
                    <ButtonLink
                      theme="light"
                      type="tertiary"
                      text={service.pageTitle}
                      link={service.slug}
                    />
                  </li>
                ))}
              {!isDesktop &&
                item.services.map((service, index) => {
                  if (index <= 4) {
                    return (
                      <li key={service.nodeID}>
                        <ButtonLink
                          theme="light"
                          type="tertiary"
                          text={service.pageTitle}
                          link={service.slug}
                        />
                      </li>
                    );
                  }

                  return (
                    <li key={service.nodeID} hidden={isServiceListHidden}>
                      <ButtonLink
                        theme="light"
                        type="tertiary"
                        text={service.pageTitle}
                        link={service.slug}
                      />
                    </li>
                  );
                })}
            </ul>
            {!isDesktop && item.services.length > 5 && (
              <>
                <div className="services-list-ellipses" hidden={!isServiceListHidden}>
                  ...
                </div>
                <div className="services-list-toggle">
                  <ButtonLink
                    isButtonTag={true}
                    theme="light"
                    type="tertiary"
                    icon="chevron_down"
                    text={isServiceListHidden ? modalListExpand : modalListCollapse} // replace with resource string
                    replacementTertiaryIcon="add"
                    onClickFunction={() => setIsServiceListHidden(!isServiceListHidden)}
                  />
                </div>
              </>
            )}
          </div>
          <ButtonLink
            theme="light"
            type="primary"
            size="medium"
            text={formatStringWithServiceAreaName(item.pageTitle, modalCtaText).toUpperCase()}
            link={item.slug}
          />
        </ModalContentStyled>
      </BdoModal>
    </ServiceLandingTabContentStyled>
  );
};

export default ServiceLandingTabContent;
